import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Filters,
  Form,
  FormFooter,
  FormRow,
  FormSection,
  Page,
  PageHeader,
  Pagination,
  linkTextClasses,
  Select,
  Table,
  type Action,
  type FilterFieldSlot,
} from '@evogard/ui';
import {
  SALE_EXPENSE_TYPE_LABELS,
  count,
  formatInstant,
  loadFailureMessage,
} from '@evogard/domain';
import { PLATE_CLASSIFICATIONS } from '@evogard/contract';
import type {
  SaleAction,
  SalesImport,
  WirePlateClassification,
  Period,
  Sale,
  SaleExpenseDetail,
} from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { can, useSessionContext } from '../../lib/session.js';
import { SaleExpenseFormModal } from './SaleExpenseFormModal.js';
import {
  PeriodField,
  currentPeriod,
  CLASSIFICATION_LABEL,
  NO_ROSTER_ACCESS,
  MoneyValue,
  periodLabel,
  useConsultants,
} from './common.js';

/**
 * As vendas da base — a lista, o registro à mão e as duas correções.
 *
 * ## O valor da venda NÃO é a comissão de ninguém, e a tela repete isso
 *
 * A comissão é **R$ fixo por placa**, com a faixa escolhida pelo total do mês.
 * Não existe percentual sobre o valor da venda em lugar nenhum desta casa — e a
 * leitura errada é a confortável: uma coluna "Valor" numa tela de comercial
 * convida qualquer pessoa a multiplicá-la por alguma coisa. Por isso a coluna
 * diz o que é, e o cabeçalho diz o que ela não é.
 *
 * ## Casar uma venda com um consultor MOVE DINHEIRO, e a tela avisa antes
 *
 * É o único caminho pelo qual uma comissão muda de pessoa. E ele não muda um
 * pouco: a faixa é escolhida pelo total do mês, então uma placa a mais pode
 * valer R$ 250,00 (a 25ª, que tira o consultor de faixa nenhuma) ou R$ 350,00
 * (o degrau entre 109 e 110 placas) — nos **dois** consultores, o que perde e o
 * que ganha. O levantamento mediu uma diferença de R$ 227,00 numa venda movida
 * seis meses depois.
 *
 * Desde 13/10/2026 a **P11** responde: a transferência vale a partir da data em
 * que acontece. A competência já apurada continua pagando quem a apuração
 * carimbou (`assessed_consultant_id`), e reapurá-la devolve o mesmo número; a
 * que ainda não foi apurada passa a contar a placa para o novo dono. O aviso diz
 * as duas metades, porque a tela não sabe qual delas vale para esta venda.
 *
 * ## A classificação da placa se declara em CORRIGIR, e só nele
 *
 * A classificação (venda, troca, Plano Bronze, reativação) decide o que conta e
 * quanto paga, e a coluna nasceu anulável em 28/08 (P12): nulo é "ninguém
 * declarou", e uma placa nula com dono faz a apuração da competência inteira ser
 * recusada — não descartada, recusada, porque descartar mudaria a contagem que
 * escolhe a faixa e a tela mostraria um valor menor sem nada explicando.
 *
 * O contrato a exige no ato `corrigir` (obrigatória e **não** anulável) e não a
 * carrega em `recordSale`. Os dois formulários desta tela seguem essa
 * divisão à letra, e cada um diz o que é verdade nele:
 *
 *   · **registrar** grava sem categoria, e o formulário avisa antes de gravar
 *     que a venda nasce assim e o que isso trava;
 *   · **corrigir** obriga a declará-la, e não oferece o caminho de volta para
 *     "ninguém declarou" — devolver a venda àquele estado não conserta nada,
 *     deixa a competência inapurável de novo sem ninguém ter pedido.
 *
 * O que continua valendo é o cuidado que a versão anterior desta nota tinha:
 * mudar a categoria de uma venda de março em abril muda quanto alguém recebeu em
 * março. Por isso a correção é um registro por vez, com a pessoa à frente dele —
 * e nunca uma releitura de texto a cada abertura de tela, que é o que o sistema
 * antigo faz e é como editar uma observação em setembro muda o pagamento de
 * março.
 *
 * ## `situacao` é texto, e é regra 14
 *
 * A lista de situações do sistema antigo nunca foi lida. Um seletor com opções
 * inventadas aqui recusaria exatamente a linha que a importação precisa gravar,
 * e daria à casa um vocabulário que ela não usa. O filtro é campo livre, e a
 * tela diz de onde o texto vem.
 */
export function SalesScreen() {
  const { data: context } = useSessionContext();
  const canRecord = can(context, 'sales.sales', 'registrar');
  const canImport = can(context, 'sales.sales', 'importar');
  const canEdit = can(context, 'sales.sales', 'editar');
  const hasLaunchExpense = can(context, 'sales.expenses', 'lancar');
  /*
   * `lancar` não basta (05/10/2026): lançar é do consultor do time interno, ou
   * de quem recebeu a exceção `lancar_sem_ser_interno`. Quem responde é a API
   * — a mesma função que `POST /sales/expenses` pergunta —, e não uma leitura
   * do perfil aqui. Enquanto ela não responde, o item não aparece.
   */
  const launchAccess = useQuery({
    queryKey: ['sale-expenses', 'launch-access', context?.currentTenant.id],
    enabled: hasLaunchExpense,
    queryFn: async () =>
      ok(
        await api.saleExpenses.launchAccess(),
        200,
        'Não foi possível saber se você pode lançar despesa.',
      ),
  });
  const canLaunchExpense = hasLaunchExpense && launchAccess.data?.allowed === true;
  const canSeeRecord = can(context, 'sales.teams', 'ver');
  /*
   * A MESMA função que o guard usa (`canAccess`, via `can`), e não uma regra
   * escrita de novo aqui. Esconder não é proteger — quem protege é o `where` de
   * `listSales`, que já devolveu só a carteira da pessoa. Isto decide o que a
   * tela **oferece**: filtro que não pode mudar nada e coluna que repete o mesmo
   * nome em toda linha.
   */
  const canSeeAny = can(context, 'sales.sales', 'ver_qualquer');

  /**
   * A competência de hoje, fixada na MONTAGEM da tela.
   *
   * Fixada na montagem, e não lida a cada render: uma aba aberta na virada do
   * mês passaria a comparar o filtro contra um mês diferente do que ela abriu.
   *
   * Até 17/09 ela era duas coisas — o estado inicial E o alvo do "Limpar tudo"
   * do `FilterCard`. Com o painel de `Filters`, limpar aplica o VAZIO, como em
   * toda tela da casa, e o vazio aqui já tinha significado declarado: traz
   * todas as competências. Sobrou o primeiro uso.
   */
  const [thisMonth] = useState(currentPeriod);
  /*
   * **A tela abre na competência corrente, e não em "todas".**
   *
   * Uma base com 384 vendas abria listando as 384, e a primeira coisa que a
   * operação fazia era digitar o mês. O vazio continua existindo e continua
   * trazendo todas — é o que o `help` do campo diz —, mas deixou de ser o
   * estado em que a tela nasce.
   */
  const [period, setPeriod] = useState<Period | ''>(thisMonth);
  const [consultantId, setConsultantId] = useState('');
  const [withoutConsultant, setWithoutConsultant] = useState(false);
  const [statusDigitada, setStatusDigitada] = useState('');
  /*
   * Vazio é "todas", e não "nenhuma" — a rota recebe o campo AUSENTE. Um array
   * vazio enviado viraria `in ()` no servidor, e a tela mostraria zero linhas
   * com o filtro visivelmente desligado.
   */
  const [classification, setClassification] = useState<WirePlateClassification[]>([]);
  const [recording, setRecording] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  /* A venda cuja despesa está sendo lançada, e a última lançada — a frase de sucesso. */
  const [expenseFor, setExpenseFor] = useState<Sale | null>(null);
  const [launched, setLaunched] = useState<SaleExpenseDetail | null>(null);

  /*
   * Dez linhas, e não as cinquenta que esta tela pedia.
   *
   * O cinquenta nunca foi uma escolha desta lista: é o `default` de
   * `paginationQuery` no contrato, que toda tela da casa herdou por pedir uma
   * página só. Com paginador de verdade a pergunta passa a ser quantas linhas
   * cabem numa tela sem rolar, e a resposta é dez — o mesmo número do modal do
   * ranking.
   *
   * O valor escolhido **não persiste** entre visitas, como nenhum outro filtro
   * desta tela: todos vivem em `useState` e morrem com ela. Guardá-lo seria
   * decisão de produto à parte, e alcançaria muito mais gente do que esta lista.
   */
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  /*
   * A situação só vai ao servidor quando a digitação para. Sem isso, um texto de
   * vinte caracteres são vinte requisições, vinte entradas de cache e, em rede
   * ruim, respostas chegando fora da ordem em que foram pedidas.
   */
  const situacao = useDebouncedTerm(statusDigitada);

  const tenantId = context?.currentTenant.id;
  const consultants = useConsultants({ enabled: canSeeRecord });

  /*
   * Estreitar o recorte encurta a lista, e a página em que se estava deixa de
   * existir: filtrar por uma competência estando na página 4 pediria um
   * `offset` além do fim, e a resposta seria uma tabela vazia sob um cartão
   * dizendo "12 vendas" — a tela negando o que ela mesma acabou de afirmar.
   *
   * `perPage` está aqui pelo mesmo motivo, e `tenantId` também: trocar de praça
   * troca a lista inteira.
   */
  useEffect(() => {
    setPage(1);
  }, [
    tenantId,
    period,
    consultantId,
    withoutConsultant,
    situacao,
    classification,
    perPage,
  ]);

  const salesQuery = useQuery({
    queryKey: [
      'comercial',
      'vendas',
      tenantId,
      period,
      consultantId,
      withoutConsultant,
      situacao,
      classification,
      page,
      perPage,
    ],
    queryFn: async () => {
      const r = await api.sales.listSales({
        query: {
          page,
          perPage,
          period: period || undefined,
          consultantId: consultantId ? Number(consultantId) : undefined,
          withoutConsultant: withoutConsultant ? 'true' : undefined,
          situacao: situacao.trim() || undefined,
          /*
           * A lista vira texto separado por vírgula aqui, e não no cliente da
           * API: é o formato que o contrato declara, e a conversão mora do lado
           * de quem conhece as duas pontas. Ver o campo em `listSales`.
           */
          classification:
            classification.length > 0 ? classification.join(',') : undefined,
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'as vendas desta base'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const total = salesQuery.data?.total;
  const sale = salesQuery.data?.items.find((v) => v.id === editing);
  const hasFilter = Boolean(
    period ||
    consultantId ||
    withoutConsultant ||
    situacao.trim() ||
    classification.length,
  );
  /*
   * O recorte no painel de `Filters`, e não mais num `FilterCard` entre o
   * cabeçalho e a tabela.
   *
   * **A situação saiu junto e foi para a BARRA.** Ela é o único recorte de
   * texto livre desta tela, e é o único cujo resultado a pessoa usa para
   * decidir o que digitar em seguida — a casa não tem lista de situações, então
   * descobrir como a origem escreveu "ativa" é a própria busca. Atrás de um
   * botão de confirmar ela deixaria de servir para isso.
   *
   * **E o "Limpar" agora esvazia a competência, em vez de devolvê-la ao mês
   * corrente.** É o que o painel faz em toda tela — ele aplica o vazio —, e o
   * vazio aqui já tinha significado declarado: traz todas. O caminho de volta
   * ao mês corrente continua sendo escolher o mês, que é o gesto que a pessoa
   * faria de qualquer forma; o que se perde é um botão que limpava para um
   * estado diferente do que ele mostrava.
   */
  const fields: FilterFieldSlot[] = [
    {
      type: 'periodo',
      name: 'period',
      label: 'Competência',
      help: 'Vazio traz todas.',
    },
    /*
     * Sem `sales.teams:ver` o campo some, em vez de virar um seletor vazio
     * dizendo que a base não tem consultor. As duas coisas parecem iguais na
     * tela e são opostas: uma é falta de permissão, a outra é falta de cadastro.
     *
     * E sem `sales.sales:ver_qualquer` ele some também, por outro motivo: a
     * lista já é de uma pessoa só. Um seletor com "Todos" e trinta nomes sobre
     * uma resposta que o servidor estreitou seria a tela oferecendo um recorte
     * que ela não pode fazer — escolher o colega devolveria vazio, e quem
     * escolheu concluiria que aquela pessoa não vendeu nada.
     */
    canSeeRecord &&
      canSeeAny && {
        type: 'selecao' as const,
        name: 'consultantId',
        label: 'Consultor',
        placeholder: 'Todos',
        help: '“Só as sem consultor” ganha deste campo.',
        options: (consultants.data ?? []).map((c) => ({
          value: String(c.id),
          label: `${c.user.nome} · ${c.erpVolunteerCode}`,
        })),
      },
    /*
     * "Sem consultor" é `selecao` de duas opções, e não uma caixa de marcar:
     * dentro de um painel a caixa não diz qual é o outro estado dela. Ver o
     * cabeçalho de `FilterField`.
     *
     * No escopo estreito ele **não aparece**, e não por simetria com o campo
     * acima: a fila de vendas sem dono não é de ninguém, então ligá-lo sobre a
     * própria carteira devolveria vazio sempre. Um filtro cuja resposta nunca
     * muda é pior do que filtro nenhum — ele ensina que a fila está limpa.
     */
    canSeeAny && {
      type: 'selecao' as const,
      name: 'withoutConsultant',
      label: 'Vendas sem consultor',
      placeholder: 'Com ou sem',
      options: [{ value: 'sim', label: 'Só as sem consultor' }],
    },
    /*
     * É o filtro que a operação usa para separar o que a comissão conta do que
     * ela ignora: a reativação não entra no ranking (P5) e é paga por outra
     * régua (§4.2, caso C), então "quanto disso é reativação" é a pergunta que
     * a mesa faz sobre um mês fechado.
     *
     * **O `help` não é enfeite.** Marcar as quatro traz MENOS linhas do que não
     * marcar nenhuma, porque a venda sem categoria declarada não é uma das
     * quatro — e essa é justamente a que trava a apuração da competência. Sem a
     * frase, quem contasse as linhas de um recorte completo concluiria que a
     * base tem menos vendas do que o cartão diz.
     */
    {
      type: 'multipla',
      name: 'classification',
      label: 'Classificação de venda',
      placeholder: 'Todas',
      wide: true,
      options: PLATE_CLASSIFICATIONS.map((c) => ({
        value: c,
        label: CLASSIFICATION_LABEL[c],
      })),
      help: 'A venda sem categoria declarada fica fora de qualquer seleção — ela aparece com o selo “Sem classificação” quando nenhuma está escolhida.',
    },
  ];

  return (
    <Page measure="cheia">
      <PageHeader
        title="Vendas"
        actions={
          <>
            {/* Esconder é cortesia; quem protege é o guard. A rota continua exigindo
                        `sales.sales:registrar`. */}
            {canImport && <ImportFromErp period={period === '' ? thisMonth : period} />}
            {canRecord && (
              <Button onClick={() => setRecording(true)}>Registrar venda</Button>
            )}
          </>
        }
      >
        {/*
         * O escopo entra no subtítulo, e não num selo ao lado do título.
         *
         * Quem só vê a própria carteira precisa saber disso ANTES de contar as
         * linhas — senão a tela é indistinguível de uma base com onze vendas, e
         * a conclusão silenciosa é que a importação falhou. É a mesma frase que
         * a tabela repete quando está vazia, e as duas existem porque nenhuma
         * delas aparece sempre.
         */}
        {canSeeAny ? 'As placas cadastradas em ' : 'As suas placas em '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        {canSeeAny
          ? ' '
          : ' Esta lista é a sua carteira: as vendas casadas com você nesta base, e nenhuma outra. '}
        O valor de uma venda não é a comissão de ninguém: a comissão é um valor fixo por
        placa, escolhido pelo total do mês.
      </PageHeader>

      {launched && (
        <Alert tone="sucesso" title="Despesa lançada">
          {SALE_EXPENSE_TYPE_LABELS[launched.type]} da placa {launched.plate} está em
          "Despesas Recebidas".{' '}
          {can(context, 'sales.expenses', 'ver_minhas') && (
            <Link
              to="/sales/expenses/mine"
              search={{ card: launched.id }}
              className={linkTextClasses()}
            >
              Acompanhar em Minhas despesas
            </Link>
          )}
        </Alert>
      )}

      {recording && canRecord && (
        <SaleForm
          consultants={consultants.data ?? []}
          canSeeRecord={canSeeRecord}
          toClose={() => setRecording(false)}
        />
      )}

      {canEdit && sale && (
        <SaleEditor
          key={sale.id}
          sale={sale}
          consultants={consultants.data ?? []}
          canSeeRecord={canSeeRecord}
          toClose={() => setEditing(null)}
        />
      )}

      {salesQuery.isError && <Alert tone="erro">{salesQuery.error.message}</Alert>}

      {/*
       * Com a consulta em erro, `isLoading` é falso e `itens` é vazio — e a
       * tabela anunciaria "nenhuma venda nesta base" logo abaixo do aviso
       * dizendo que não deu para carregar. Duas afirmações contrárias na mesma
       * tela, e a de baixo é a que parece um fato.
       */}
      {(!salesQuery.isError || salesQuery.data) && (
        <Card
          title={total === undefined ? 'Carregando…' : count(total, 'venda', 'vendas')}
          /*
           * A competência entra no cartão porque o número acima é dela, e desde
           * que a tela abre filtrada ele deixou de ser o total da base.
           *
           * Sem esta linha, uma praça com 384 vendas mostraria "23 vendas" na
           * primeira abertura, sem nada dizendo que são as de setembro — e
           * "quantas vendas esta base tem?" passaria a ter uma resposta errada
           * escrita em negrito no topo da lista.
           */
          descricao={
            period
              ? `Na competência de ${periodLabel(period)}.`
              : 'Todas as competências.'
          }
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar pela situação da venda',
              placeholder: 'Situação, como a origem escreveu',
              value: statusDigitada,
              onChange: setStatusDigitada,
            }}
            toolbar={
              <Filters
                values={{
                  period,
                  consultantId,
                  withoutConsultant: withoutConsultant ? 'sim' : '',
                  classification,
                }}
                fields={fields}
                onApply={(values) => {
                  /*
                   * "Só as sem consultor" ganha do nome escolhido, e o campo do
                   * consultor é zerado junto. Os dois recortes são contrários —
                   * mandar os dois ao servidor devolveria vazio sempre, e quem
                   * escolheu concluiria que aquela pessoa não vendeu nada.
                   */
                  const none = values.withoutConsultant === 'sim';
                  setPeriod((String(values.period ?? '') || '') as Period | '');
                  setWithoutConsultant(none);
                  setConsultantId(none ? '' : String(values.consultantId ?? ''));
                  setClassification(
                    (Array.isArray(values.classification)
                      ? values.classification
                      : []) as WirePlateClassification[],
                  );
                }}
              />
            }
            label="Vendas da competência"
            items={salesQuery.data?.items ?? []}
            loading={salesQuery.isLoading}
            /*
             * O esqueleto tem o tamanho da página, e não as três linhas de
             * fábrica: com dez por página, três linhas cinzentas viravam dez de
             * conteúdo e o cartão crescia sob o ponteiro — o que estava embaixo
             * dele pula no instante em que a pessoa vai clicar.
             */
            skeletonRows={perPage}
            rowKey={(v) => String(v.id)}
            /* O painel das reticências se chama pela placa, que é como se procura a venda. */
            rowTitle={(v) =>
              `Venda ${v.vehicle?.placa ?? `do veículo ${v.codigoVeiculo ?? '—'}`}`
            }
            rowSubtitle={(v) =>
              `${periodLabel(v.period)} · ${v.idExterno === null ? 'registrada aqui' : `importada · ${v.idExterno}`}`
            }
            rowIcon="comercial"
            /*
             * As ações da linha no painel das reticências, e não mais um botão
             * "Corrigir" numa coluna chamada "Ação".
             *
             * **Não há "Excluir", e a ausência é do CONTRATO, não desta tela.**
             * `comercial_vendas.arquivado_em` existe para a correção que a
             * importação vier a precisar, e nenhuma rota o grava — está escrito
             * no `saleActionSchema`. Desenhar o item aqui, mesmo desabilitado,
             * seria a tela oferecendo um caminho que não existe; quando a
             * importação pedir o ato, ele nasce na tarefa que o escrever e o
             * item entra aqui com ele.
             *
             * Corrigir continua abrindo o editor ACIMA da lista, e não um
             * formulário dentro do painel: são dois atos com dois botões
             * próprios — os campos da venda e o dono dela —, e um deles move
             * comissão de um mês inteiro de pessoa. Isso não cabe num painel
             * que se fecha ao rodar.
             */
            rowActions={(v): Action[] => [
              ...(canEdit
                ? [
                    {
                      label: 'Corrigir',
                      icon: 'etiqueta' as const,
                      run: () => setEditing(v.id),
                    },
                  ]
                : []),
              /*
               * A despesa da venda (02/10/2026) — indicação, troca, vistoria
               * extra, inativo e reembolso. Abre o formulário do tipo, e o
               * cartão nasce em "Despesas Recebidas" no quadro do Financeiro.
               */
              ...(canLaunchExpense
                ? [
                    {
                      label: 'Lançar despesa',
                      icon: 'dinheiro' as const,
                      run: () => setExpenseFor(v),
                    },
                  ]
                : []),
            ]}
            /*
             * Três vazios, e não dois, porque no escopo estreito os dois
             * antigos ficam falsos: "nenhuma venda nesta base ainda" seria a
             * tela afirmando sobre a praça o que ela mediu sobre uma pessoa — e
             * a base pode ter trezentas. Quem lê precisa saber se o que falta é
             * venda sua, venda da casa, ou só o recorte.
             */
            empty={
              hasFilter
                ? 'Nenhuma venda com esses filtros. Isso é sobre o recorte, não sobre a base.'
                : canSeeAny
                  ? /*
                     * **Esta frase já foi uma mentira**, e a correção é o
                     * assunto dela.
                     *
                     * Ela dizia "Nenhuma venda nesta base ainda", que é uma
                     * afirmação sobre a BASE. Medido em 21/09/2026: a CE tinha
                     * 15.056 contratos de 2026 no espelho e zero venda aqui,
                     * porque a importação morria todo dia às 5h — e falha de
                     * job não tem tela. A frase ficava de pé, e quem a lia
                     * concluía que o ERP estava vazio.
                     *
                     * Agora ela afirma só o que esta consulta mediu — "nenhuma
                     * venda IMPORTADA nesta competência" — e diz onde está a
                     * resposta: o botão, que roda a importação na hora e
                     * devolve o motivo quando ela recusa.
                     */
                    'Nenhuma venda importada nesta competência. Elas entram pela importação do ERP (todo dia às 5h, ou pelo botão “Importar do ERP”) ou uma a uma por “Registrar venda”. Se o ERP tem contrato nesta competência e nada aparece aqui, a importação está recusando — o botão diz por quê.'
                  : 'Nenhuma venda casada com você nesta base. Isso não diz nada sobre a base — ela pode ter vendas de outras pessoas, e você não as enxerga aqui. Se houver venda sua que não aparece, é o vínculo com o código do ERP que falta, e quem o resolve é quem cuida do cadastro de consultores.'
            }
            /*
             * **A ordem das colunas é a ordem da pergunta** (22/10/2026).
             *
             * Até aqui a linha abria pela competência e fechava pelo que foi
             * vendido — e, com a tela nascendo filtrada no mês, a primeira
             * coluna repetia "outubro de 2026" em toda linha enquanto a placa,
             * que é por onde se procura uma venda, ficava cortada na borda
             * direita a 1440px. Agora a linha abre pela placa e pela pessoa,
             * passa por quem vendeu e o que a placa é, e termina no valor e na
             * procedência.
             *
             * **A situação deixou de ser coluna.** Na CE ela vem nula em toda
             * venda importada, e a coluna era uma faixa de 10rem dizendo "a
             * origem não disse" trinta vezes. Quando a origem diz, ela aparece
             * embaixo da procedência; a busca da barra continua sendo por ela.
             */
            columns={[
              {
                /*
                 * Era "Códigos", e mostrava `Associado 26324 · Veículo 88053`.
                 *
                 * O comentário que estava aqui dizia que a placa não aparecia
                 * porque "o espelho está vazio nas duas praças" — deixou de
                 * ser verdade na CE em 21/09/2026, com 76.745 veículos e
                 * 52.379 associados espelhados. Com o espelho cheio, o código
                 * cru obrigava quem atende a abrir o SGA para saber de quem é
                 * a venda que está olhando.
                 */
                title: 'O que foi vendido',
                alignment: 'inicio',
                minWidth: '15rem',
                cell: (v) => <WhatWasSold sale={v} />,
              },
              /*
               * A coluna do dono some no escopo estreito, e é a única das três
               * ausências que não é sobre filtro.
               *
               * Toda linha traria o mesmo nome — o de quem está lendo —, e uma
               * coluna de 13rem repetindo a mesma coisa trinta vezes gasta a
               * largura de que as outras precisam no telefone. O selo "Sem
               * consultor" também não tem como aparecer aqui: venda sem dono
               * nunca entra nesta carteira.
               */
              ...(canSeeAny
                ? [
                    {
                      title: 'Consultor',
                      alignment: 'inicio' as const,
                      minWidth: '13rem',
                      cell: (v: Sale) =>
                        v.consultant ? (
                          <div className="min-w-0">
                            <p className="truncate">{v.consultant.user.nome}</p>
                            <p className="mt-0.5 truncate text-xs tabular-nums text-texto-suave">
                              código {v.consultant.erpVolunteerCode}
                            </p>
                          </div>
                        ) : (
                          /*
                           * Selo, e não texto cinza: é fila de trabalho, e é
                           * dinheiro que ninguém vai receber enquanto estiver
                           * assim. O tom é `alerta` — cor, ícone e texto, os
                           * três.
                           *
                           * E o código do voluntário embaixo, quando a origem o
                           * mandou: sem ele o selo diz que há trabalho a fazer
                           * sem dizer **qual**, e quem atende precisaria abrir o
                           * SGA pelo código do veículo para descobrir quem
                           * cadastrar. Nulo é a venda digitada na tela, onde
                           * ninguém informou voluntário nenhum — e aí o selo
                           * sozinho é a resposta inteira.
                           */
                          <div className="min-w-0">
                            <Badge tone="alerta">Sem consultor</Badge>
                            {v.volunteerCode !== null && (
                              <p className="mt-1 truncate text-xs tabular-nums text-texto-suave">
                                voluntário {v.volunteerCode} no ERP
                              </p>
                            )}
                          </div>
                        ),
                    },
                  ]
                : []),
              {
                title: 'Classificação',
                minWidth: '10rem',
                cell: (v) => <ClassificationBadge sale={v} />,
              },
              {
                title: 'Valor da venda',
                alignment: 'fim',
                cell: (v) => <MoneyValue value={v.value} />,
              },
              {
                title: 'Competência',
                alignment: 'inicio',
                minWidth: '11rem',
                cell: (v) => (
                  <div className="min-w-0">
                    <p className="tabular-nums">{periodLabel(v.period)}</p>
                    <p className="mt-0.5 truncate text-xs text-texto-suave">
                      {/*
                       * `idExterno` nulo responde inteira a pergunta "de onde
                       * veio esta linha" — não há segunda coluna dizendo o
                       * mesmo. Preenchido, ele é a identidade da venda no
                       * sistema de origem, e reenviá-lo atualiza esta linha.
                       */}
                      {v.idExterno === null ? (
                        'registrada aqui'
                      ) : (
                        <>importada · {v.idExterno}</>
                      )}
                    </p>
                    {/* nula é "a origem não disse", e a ausência da linha diz isso */}
                    {v.situacao !== null && (
                      <p className="mt-0.5 truncate text-xs text-texto-suave">
                        situação {v.situacao}
                      </p>
                    )}
                  </div>
                ),
              },
            ]}
          />

          <Pagination
            /*
             * O estado LOCAL, e não o `page` que a resposta devolve.
             *
             * A resposta é a da página anterior enquanto a nova não chega — o
             * `placeholderData` é o que mantém as linhas na tela em vez de
             * piscar um esqueleto a cada clique. Alimentar o paginador com ela
             * faria a conta de "próxima" partir do número velho: dois cliques
             * seguidos pediriam a página 2 duas vezes, e o segundo não iria a
             * lugar nenhum.
             */
            page={page}
            perPage={perPage}
            /*
             * Sem resposta ainda não há o que paginar, e um `0` aqui esconde o
             * paginador inteiro — que é o certo: desenhá-lo antes da primeira
             * resposta seria a tela afirmando um total que ela não tem.
             */
            total={total ?? 0}
            onChange={setPage}
            onPerPageChange={setPerPage}
            label="vendas"
            /* o cartão é `bleed`; o espaçamento é de quem entra nele */
            className="border-t border-borda-suave px-5 py-4"
          />
        </Card>
      )}

      {expenseFor && (
        <SaleExpenseFormModal
          mode={{ kind: 'lancar', sale: expenseFor }}
          onClose={() => setExpenseFor(null)}
          onSaved={(detail) => {
            setExpenseFor(null);
            setLaunched(detail);
          }}
        />
      )}
    </Page>
  );
}

/* ------------------------------------------------------------------------- *
 * Importar do ERP — o botão que faltava, e a recusa que ninguém via
 * ------------------------------------------------------------------------- */

/**
 * Roda a importação da competência a partir do espelho, agora.
 *
 * ## Por que ela precisa existir
 *
 * A importação só saía do cron das 5h, e quando ela recusava **nada dizia**:
 * falha de job não tem tela. A consequência foi medida em 21/09/2026 — 15.056
 * contratos de 2026 no espelho da CE, zero venda aqui, e a tabela anunciando
 * "nenhuma venda nesta base ainda" como se o ERP estivesse vazio.
 *
 * ## Por que o desfecho aparece aqui, e não some num toast
 *
 * Porque os dois desfechos são números que alguém precisa **ler e conferir**:
 * quantos contratos a competência tem, quantos viraram venda, e quantos
 * ficaram sem consultor casado. O terceiro é o que separa "a importação não
 * funcionou" de "funcionou, e o cadastro de consultores é que está atrás" — na
 * CE são 742 voluntários no ERP contra 8 consultores. Um aviso que some em
 * cinco segundos leva esse número junto.
 *
 * ## `recusada` não é erro vermelho
 *
 * Os três motivos são **ausência de condição**, não defeito: espelho vencido,
 * espelho nunca sincronizado, situação não classificada. Pintá-los de erro
 * faria a operação abrir chamado para o time errado; o tom é `alerta`, e o
 * texto diz qual é a tela que resolve.
 */
function ImportFromErp({ period }: { period: Period }) {
  const client = useQueryClient();
  const [outcome, setOutcome] = useState<SalesImport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async () => {
      const r = await api.sales.importSales({ body: { period } });
      return ok(r, 200, 'Não foi possível importar as vendas.');
    },
    onSuccess: (body) => {
      setError(null);
      setOutcome(body);
      /* Só relê a lista quando alguma linha entrou: invalidar na recusa faria
       * a tabela piscar para mostrar exatamente o que já estava lá. */
      if (body.estado === 'importada' && body.imported > 0) {
        void client.invalidateQueries({ queryKey: ['sales'] });
      }
    },
    onError: (e: Error) => {
      setOutcome(null);
      setError(e.message);
    },
  });

  return (
    <>
      <Button variant="secundario" loading={run.isPending} onClick={() => run.mutate()}>
        Importar do ERP
      </Button>

      {outcome?.estado === 'importada' && (
        <Alert
          tone="sucesso"
          title={`Competência ${periodLabel(outcome.period)} importada.`}
        >
          <p className="mt-1">
            {count(outcome.contracts, 'contrato', 'contratos')} no espelho ·{' '}
            {count(outcome.imported, 'virou venda', 'viraram venda')} ·{' '}
            {count(outcome.canceled, 'cancelado', 'cancelados')} ·{' '}
            {count(outcome.unknownAdhesion, 'sem adesão descrita', 'sem adesão descrita')}
            .
          </p>
          {outcome.withoutConsultant > 0 && (
            <p className="mt-2">
              <strong className="font-medium">
                {count(outcome.withoutConsultant, 'venda', 'vendas')} sem consultor casado
              </strong>{' '}
              — elas existem e estão certas, e não entram no Ranking nem em apuração
              nenhuma até alguém cadastrar o consultor daquele código. O filtro “Só as sem
              consultor” traz a fila, e cada linha diz de qual voluntário do ERP ela é.
            </p>
          )}
        </Alert>
      )}

      {outcome?.estado === 'recusada' && (
        <Alert tone="alerta" title="A importação não rodou, e isto é o motivo.">
          <p className="mt-1">{outcome.detail}</p>
          <p className="mt-2">{REFUSAL_WAY_OUT[outcome.reason]}</p>
        </Alert>
      )}

      {error !== null && <Alert tone="erro">{error}</Alert>}
    </>
  );
}

/**
 * A célula que diz o que a venda é: o veículo e o associado, por nome.
 *
 * ## Por que o nome vem primeiro e o código embaixo
 *
 * Porque quem lê a tabela procura a placa e a pessoa, não o identificador do
 * ERP. O código continua visível — é por ele que se confere contra o SGA e é
 * ele que se corrige na ficha —, mas em segundo plano.
 *
 * ## Sem rótulo, mostra o CÓDIGO e não escreve frase nenhuma
 *
 * `member`/`vehicle` nulos querem dizer "o rótulo não veio": a venda não tem
 * código, o espelho não tem aquele código, ou o espelho não respondeu. A
 * célula não afirma qual dos três — escrever "não está no cadastro" aqui seria
 * a tela decidindo sobre o cadastro o que ela mediu sobre a integração. Quem
 * sabe distinguir é a tela de saúde do ERP.
 */
/** `PLACA · MARCA MODELO 2020`, com o que existir e sem separador sobrando. */
function vehicleLabel(v: {
  placa: string | null;
  marca: string | null;
  modelo: string | null;
  anoModelo: string | null;
}): string | undefined {
  const model = [v.marca, v.modelo, v.anoModelo].filter(Boolean).join(' ');
  const parts = [v.placa, model || null].filter(Boolean);
  return parts.length === 0 ? undefined : parts.join(' · ');
}

/**
 * A linha embaixo do campo de código, dizendo o que aquele código é.
 *
 * ## Ela fala do valor SALVO, e cala quando alguém digita
 *
 * O campo é editável — é por ele que se corrige uma venda apontando para o
 * registro errado. Se a linha continuasse mostrando o nome antigo depois de a
 * pessoa trocar o código, ela afirmaria que o código NOVO é daquele associado,
 * que é exatamente o contrário do que ela existe para fazer. Enquanto o
 * digitado difere do salvo, ela diz que a conferência sai ao salvar.
 *
 * ## Sem rótulo, ela não desenha nada
 *
 * Não escreve "não está no espelho": o rótulo pode faltar porque o código não
 * existe no cadastro **ou** porque o espelho não respondeu, e a ficha não tem
 * como distinguir os dois. Afirmar um deles seria a tela dizendo sobre o
 * cadastro o que ela mediu sobre a integração — quem sabe a diferença é a tela
 * de saúde do ERP.
 */
function FromMirror({
  saved,
  typed,
  children,
}: {
  saved: string | null;
  typed: string;
  children: string | undefined;
}) {
  if ((saved ?? '') !== typed.trim()) {
    return (
      <p className="mt-1 text-xs text-texto-suave">
        O código mudou — a conferência sai ao salvar.
      </p>
    );
  }
  if (children === undefined || children === '') return null;

  return <p className="mt-1 text-xs text-texto">{children}</p>;
}

function WhatWasSold({ sale }: { sale: Sale }) {
  const model = [sale.vehicle?.marca, sale.vehicle?.modelo].filter(Boolean).join(' ');

  return (
    <div className="min-w-0">
      {sale.vehicle?.placa != null ? (
        <p className="truncate font-medium tabular-nums">{sale.vehicle.placa}</p>
      ) : (
        <p className="truncate text-texto-suave">Veículo {sale.codigoVeiculo ?? '—'}</p>
      )}

      {model !== '' && (
        <p className="mt-0.5 truncate text-xs text-texto-suave">
          {model}
          {sale.vehicle?.anoModelo != null && ` ${sale.vehicle.anoModelo}`}
        </p>
      )}

      <p className="mt-0.5 truncate text-xs text-texto-suave">
        {sale.member?.nome ?? `Associado ${sale.codigoAssociado ?? '—'}`}
      </p>
    </div>
  );
}

/**
 * O que fazer a respeito de cada recusa — e cada frase aponta para UMA tela.
 *
 * Um motivo sem saída é a tela dizendo "não deu" e devolvendo o problema para
 * quem está olhando. Os três têm conserto conhecido, e nenhum deles é código
 * faltando.
 */
// @valor — as chaves são os valores da união `reason`, e valor fica em pt-BR
const REFUSAL_WAY_OUT: Record<
  Extract<SalesImport, { estado: 'recusada' }>['reason'],
  string
> = {
  espelho_vencido:
    'O espelho do ERP está velho demais para uma leitura que grava. Varra a coleção de veículos em Integrações › Saúde do ERP e importe de novo — a varredura leva minutos.',
  espelho_nao_sincronizado:
    'A esteira do ERP nunca fechou nesta base, então não há cadastro de onde importar. Confira a credencial em Integrações e varra a coleção de veículos.',
  situacao_nao_classificada:
    'A casa ainda não disse o que este código de situação significa, e sem isso “cancelado não conta” não tem como ser cumprido. Classifique-o em Integrações › Situações do ERP.',
};

/**
 * O selo da classificação na lista.
 *
 * `null` é "ninguém declarou", e é fila de trabalho como "sem consultor": selo
 * de alerta, com cor, ícone e texto — não um traço cinza, que na mesma tabela
 * quer dizer "a origem não disse" e é outra coisa. A placa nula só trava a
 * apuração quando tem dono, e a linha ao lado mostra se tem.
 */
function ClassificationBadge({ sale }: { sale: Sale }) {
  if (sale.classification === null) return <Badge tone="alerta">Sem classificação</Badge>;
  return <span>{CLASSIFICATION_LABEL[sale.classification]}</span>;
}

/* ------------------------------------------------------------------------- *
 * Registrar
 * ------------------------------------------------------------------------- */

/**
 * Registrar uma venda à mão.
 *
 * Convive com a importada: a identidade é `(base, id externo)`, e reenviar o
 * mesmo identificador **atualiza** a mesma linha em vez de criar a segunda. Sem
 * identificador, a venda nasceu aqui — e duas vendas digitadas à mão são dois
 * fatos, porque a tela não tem como saber que são a mesma.
 */
function SaleForm({
  consultants,
  canSeeRecord,
  toClose,
}: {
  consultants: Array<{ id: number; user: { nome: string }; erpVolunteerCode: string }>;
  canSeeRecord: boolean;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [period, setPeriod] = useState<Period>('');
  const [value, setValue] = useState('');
  const [situacao, setStatus] = useState('');
  const [consultantId, setConsultantId] = useState('');
  const [codigoAssociado, setCodeMember] = useState('');
  const [codigoVeiculo, setCodeVehicle] = useState('');
  const [idExterno, setIdExternal] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: async () => {
      const r = await api.sales.recordSale({
        body: {
          idExterno: idExterno.trim() || null,
          period,
          /*
           * O valor vai como o texto que a pessoa digitou, na forma da coluna
           * `numeric(12,2)`. Nada de `Number(...)` no caminho: `19.99 * 100` já
           * é `1998.9999999999998`, e quem recusa o que estiver fora da forma é
           * o servidor — com a mensagem dele, não com uma escrita aqui.
           */
          value: value.trim(),
          situacao: situacao.trim() || null,
          consultantId: consultantId ? Number(consultantId) : null,
          codigoAssociado: codigoAssociado.trim() || null,
          codigoVeiculo: codigoVeiculo.trim() || null,
        },
      });
      return ok(r, 201, 'Não foi possível registrar a venda.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  /*
   * O canto direito do cabeçalho conta os OBRIGATÓRIOS, e ele é medido — não
   * escrito. São os dois que o `recordSale` recusa vazios; os outros cinco são
   * legítimos em branco, e contá-los diria "2 de 7" para um formulário que já
   * pode ser enviado.
   */
  const required = [period, value.trim()];
  const done = required.filter(Boolean).length;

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        record.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}

      <FormSection
        title="A venda"
        descricao="Uma placa, numa competência. O valor é o da venda — não o da comissão."
        validacao={`${done}/${required.length} obrigatórios`}
      >
        <FormRow cols={[4, 4, 4]}>
          <PeriodField
            value={period}
            onChange={setPeriod}
            help="O mês a que a venda pertence. É por ele que a comissão é apurada."
          />
          <Field
            label="Valor da venda"
            required
            inputMode="decimal"
            placeholder="0.00"
            prefix="R$"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            help="Em reais, com ponto: 1234.56."
          />
          <Field
            label="Situação"
            maxLength={60}
            value={situacao}
            onChange={(e) => setStatus(e.target.value)}
            help="Como o sistema de origem a escreve. Vazio é “a origem não disse”."
          />
        </FormRow>
      </FormSection>

      <FormSection
        title="A quem ela pertence"
        descricao="Quem vendeu e o que foi vendido, pelos códigos do ERP. Nada aqui é obrigatório — o que faltar deixa a venda na fila de trabalho."
      >
        <FormRow cols={[6, 3, 3]}>
          {canSeeRecord ? (
            <Select
              label="Consultor"
              value={consultantId}
              onChange={(e) => setConsultantId(e.target.value)}
              help="Sem consultor a venda fica na fila de trabalho — e não paga ninguém."
            >
              <option value="">Sem consultor</option>
              {consultants.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.user.nome} · {c.erpVolunteerCode}
                </option>
              ))}
            </Select>
          ) : (
            <div className="text-xs text-texto-suave">
              <p className="text-xs font-medium text-texto">Consultor</p>
              <p className="mt-1">{NO_ROSTER_ACCESS}</p>
              <p className="mt-1">A venda será registrada sem consultor.</p>
            </div>
          )}
          <Field
            label="Código do associado"
            maxLength={64}
            value={codigoAssociado}
            onChange={(e) => setCodeMember(e.target.value)}
          />
          <Field
            label="Código do veículo"
            maxLength={64}
            value={codigoVeiculo}
            onChange={(e) => setCodeVehicle(e.target.value)}
            help="O código, não a placa: a placa é campo do espelho do ERP."
          />
        </FormRow>

        <FormRow cols={[12]}>
          <Field
            label="Identificador na origem"
            maxLength={120}
            value={idExterno}
            onChange={(e) => setIdExternal(e.target.value)}
            help="Preenchido, reenviar o mesmo identificador atualiza esta venda em vez de criar a segunda. Vazio é “nasceu aqui”."
          />
        </FormRow>
      </FormSection>

      <FormFooter>
        <Button variant="discreto" onClick={toClose}>
          Cancelar
        </Button>
        <Button type="submit" loading={record.isPending}>
          Registrar
        </Button>
      </FormFooter>
    </Form>
  );
}

/* ------------------------------------------------------------------------- *
 * Corrigir e casar
 * ------------------------------------------------------------------------- */

/**
 * O editor de uma venda — os **dois atos**, declarados como atos.
 *
 * Eles são separados no contrato e continuam separados aqui, com botões
 * próprios. Um formulário só, que mandasse tudo junto, faria "não quis mexer no
 * consultor" e "tirei o consultor" serem a mesma requisição — e descasar uma
 * venda viraria um campo esquecido, que é a comissão de um mês inteiro mudando
 * de dono por descuido.
 *
 * Edição inline e progressiva, acima da lista, e não diálogo: corrigir um valor
 * não paga uma camada por cima da tela.
 */
function SaleEditor({
  sale,
  consultants,
  canSeeRecord,
  toClose,
}: {
  sale: Sale;
  consultants: Array<{ id: number; user: { nome: string }; erpVolunteerCode: string }>;
  canSeeRecord: boolean;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [period, setPeriod] = useState<Period>(sale.period);
  /*
   * `''` é "a origem não disse quanto" — a venda importada do espelho pode
   * chegar assim. Diferente da classificação, o valor **é** enviável vazio: o
   * ato `corrigir` aceita nulo, e digitar zero seria afirmar que a venda valeu
   * zero.
   */
  const [value, setValue] = useState(sale.value ?? '');
  const [situacao, setStatus] = useState(sale.situacao ?? '');
  /*
   * `''` é "ninguém declarou" — o estado em que a venda pode chegar — e ele NÃO
   * é enviável: o ato `corrigir` exige a classificação e não a aceita nula. O
   * `required` do seletor barra o envio no navegador; a guarda no `submit` é o
   * que estreita o tipo, e é ela que vale se o navegador não barrar.
   */
  const [classification, setClassification] = useState<WirePlateClassification | ''>(
    sale.classification ?? '',
  );
  const [codigoAssociado, setCodeMember] = useState(sale.codigoAssociado ?? '');
  const [codigoVeiculo, setCodeVehicle] = useState(sale.codigoVeiculo ?? '');
  const [consultantId, setConsultantId] = useState(
    sale.consultant ? String(sale.consultant.id) : '',
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (body: SaleAction) => {
      const r = await api.sales.editSale({
        params: { id: sale.id },
        body: body,
      });
      return ok(r, 200, 'Não foi possível salvar a venda.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const consultantChanged =
    (sale.consultant ? String(sale.consultant.id) : '') !== consultantId;

  return (
    <Card
      title="Corrigir venda"
      descricao={`Registrada em ${formatInstant(sale.createdAt)} · última alteração em ${formatInstant(sale.updatedAt)}.`}
      actions={
        <Button variant="discreto" size="sm" onClick={toClose}>
          Fechar
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="erro">{error}</Alert>}

        {/* --- ato 1: corrigir os campos da venda --- */}
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            /*
             * A classificação é o único campo do ato que não tem forma vazia:
             * "ninguém declarou" é estado de chegada, não de saída. Recusar aqui
             * dá a mensagem na tela, no campo, em vez de mandar ao servidor um
             * corpo que o contrato já sabe recusar.
             */
            if (classification === '') {
              setError('Diga o que a placa é antes de salvar a correção.');
              return;
            }
            /*
             * `corrigir` manda TODOS os campos, e nenhum é opcional: um campo
             * omitido e um campo que a pessoa apagou seriam a mesma requisição,
             * e a diferença é entre "não mexi" e "tirei".
             */
            save.mutate({
              action: 'corrigir',
              period,
              /* Vazio vira nulo, e não zero — ver o estado acima. */
              value: value.trim() === '' ? null : value.trim(),
              situacao: situacao.trim() || null,
              classification,
              codigoAssociado: codigoAssociado.trim() || null,
              codigoVeiculo: codigoVeiculo.trim() || null,
            });
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <PeriodField value={period} onChange={setPeriod} />
            <Field
              label="Valor da venda"
              required
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <Field
              label="Situação"
              maxLength={60}
              value={situacao}
              onChange={(e) => setStatus(e.target.value)}
              help="Vazio é “a origem não disse”."
            />
            {/*
             * Não há opção "sem classificação": o ato não a aceita, e oferecê-la
             * seria um caminho de volta para o estado que trava a apuração da
             * competência inteira — desfeito por quem só queria corrigir um
             * valor.
             */}
            <Select
              label="Classificação da placa"
              required
              value={classification}
              onChange={(e) =>
                setClassification(e.target.value as WirePlateClassification | '')
              }
              help={
                sale.classification === null
                  ? 'Esta venda está sem classificação, e é ela que faz a competência inteira recusar apurar. Salvar a correção exige declará-la.'
                  : 'É ela que decide o que conta e quanto paga. Mudá-la muda a comissão da competência desta venda.'
              }
            >
              <option value="">Escolha…</option>
              {PLATE_CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>
                  {CLASSIFICATION_LABEL[c]}
                </option>
              ))}
            </Select>
            <div>
              <Field
                label="Código do associado"
                maxLength={64}
                value={codigoAssociado}
                onChange={(e) => setCodeMember(e.target.value)}
              />
              <FromMirror saved={sale.codigoAssociado} typed={codigoAssociado}>
                {sale.member?.nome ?? undefined}
              </FromMirror>
            </div>
            <div>
              <Field
                label="Código do veículo"
                maxLength={64}
                value={codigoVeiculo}
                onChange={(e) => setCodeVehicle(e.target.value)}
              />
              <FromMirror saved={sale.codigoVeiculo} typed={codigoVeiculo}>
                {sale.vehicle === null ? undefined : vehicleLabel(sale.vehicle)}
              </FromMirror>
            </div>
          </div>
          <div>
            <Button type="submit" loading={save.isPending}>
              Salvar a correção
            </Button>
          </div>
        </form>

        {/* --- ato 2: casar (ou descasar) com um consultor --- */}
        <div className="flex flex-col gap-3 border-t border-borda pt-4">
          <div>
            <h3 className="text-sm font-semibold">De quem é esta venda</h3>
            <p className="mt-0.5 text-xs text-texto-suave">
              Ato separado, e com um botão próprio — mudar o dono de uma venda não é
              corrigir um campo dela.
            </p>
          </div>

          {/*
           * Sem `sales.teams:ver` NADA disto aparece — nem o aviso, nem o
           * seletor, nem o botão.
           *
           * Até 21/09/2026 o aviso do dinheiro e o botão desabilitado eram
           * desenhados assim mesmo, e o resultado era uma tela explicando, em
           * três parágrafos, as consequências de um ato que quem está olhando
           * **não pode executar** — o degrau de R$ 350,00, quem perde, quem
           * ganha, e a reapuração que não acontece sozinha. Depois disso um
           * botão cinza que nunca habilita.
           *
           * Aviso de consequência serve para quem decide. Para quem não pode
           * agir, ele é barulho que faz a pessoa procurar o que fazer com uma
           * informação que não é dela — e o botão morto sugere que falta um
           * passo, quando o que falta é uma permissão.
           *
           * O que fica é o cabeçalho (a ficha continua dizendo que este ato
           * existe, e não some do produto) e a frase que nomeia a permissão
           * exata a pedir.
           */}
          {canSeeRecord ? (
            <>
              <div className="max-w-sm">
                <Select
                  label="Consultor"
                  value={consultantId}
                  onChange={(e) => setConsultantId(e.target.value)}
                >
                  <option value="">Sem consultor — volta para a fila de trabalho</option>
                  {consultants.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.user.nome} · {c.erpVolunteerCode}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <Button
                  variant="contorno"
                  loading={save.isPending}
                  /*
                   * Desabilitado enquanto nada mudou: o botão que grava o que já
                   * está gravado convida a clicar "para garantir", e aqui cada
                   * gravação é um evento na trilha dizendo que alguém mexeu no
                   * dono de uma comissão.
                   *
                   * `canSeeRecord` saiu daqui: ele não desabilita mais, ele
                   * esconde o bloco inteiro. Manter os dois faria a condição
                   * viver em dois lugares, e o botão sobreviveria ao próximo
                   * refatoramento do ramo de cima.
                   */
                  disabled={!consultantChanged}
                  onClick={() => {
                    setError(null);
                    save.mutate({
                      action: 'casar_consultor',
                      consultantId: consultantId ? Number(consultantId) : null,
                    });
                  }}
                >
                  {consultantId ? 'Casar com este consultor' : 'Descasar do consultor'}
                </Button>
              </div>
            </>
          ) : (
            <Alert tone="informacao">{NO_ROSTER_ACCESS}</Alert>
          )}
        </div>
      </div>
    </Card>
  );
}
